import type {
  Channel,
  ChannelType,
  InstagramConfig,
  MessengerConfig,
  PhoneConfig,
  PhoneProvider,
  TikTokConfig,
  WhatsappConfig,
} from "@/types/channel";

import { faker } from "@faker-js/faker";

import { getMockMarketProfile } from "../market";

import {
  createAvatarUrl,
  createId,
  createPastDate,
  createPhoneNumber,
} from "./base";

const MARKET = getMockMarketProfile();

interface ChannelConfigItem {
  name: string;
  type: ChannelType;
  provider?: PhoneProvider;
  isLandline?: boolean;
  isCoexistence?: boolean;
}

const CLINIC_CHANNELS: ChannelConfigItem[] = [
  { name: `${MARKET.organizationName} Instagram`, type: "instagram" },
  { name: "Clinic WhatsApp", type: "whatsapp" },
  { name: "Appointments WhatsApp", type: "whatsapp", isCoexistence: true },
  { name: "Clinic Phone", type: "phone", provider: "du", isLandline: false },
  {
    name: "Clinic Landline",
    type: "phone",
    provider: "etisalat",
    isLandline: true,
  },
];

function generateInstagramConfig(): InstagramConfig {
  return {
    isTokenExpired: false,
    username: MARKET.organizationSlug.replaceAll("-", ""),
    userId: faker.string.numeric(15),
    id: faker.string.numeric(15),
    mediaCount: faker.number.int({ min: 50, max: 200 }),
    profilePictureUrl: createAvatarUrl(),
    biography:
      "Beauty Redefined ✨ Expert Skincare | Hair Transplant | Aesthetic Solutions Inspiring Confidence Every Step! ⧫ Book Your Glow-Up Today!",
    followersCount: faker.number.int({ min: 5000, max: 20000 }),
    followsCount: faker.number.int({ min: 200, max: 500 }),
    token: faker.string.alphanumeric(64),
  };
}

function generateWhatsappConfig(
  name: string,
  isCoexistence: boolean = false,
): WhatsappConfig {
  return {
    token: faker.string.alphanumeric(64),
    isTokenExpired: false,
    ...(isCoexistence
      ? {
          isCoexistence: true,
          coexistenceSync: { state: "syncing" as const, progress: 40 },
        }
      : {
          whatsappProfile: {
            messagingProduct: "whatsapp",
            about: "Dermatology and aesthetics. Open Sat to Thu, 9 to 7.",
            address: "Marina Plaza, Level 12, Dubai Marina",
            description: `${MARKET.organizationName}: dermatology, laser and aesthetics.`,
            email: `hello@${MARKET.organizationSlug}.com`,
            websites: [`https://${MARKET.organizationSlug}.com`],
            vertical: "health" as const,
            profilePictureUrl: null,
          },
        }),
    phoneNumber: {
      id: faker.string.numeric(15),
      displayPhoneNumber: createPhoneNumber(),
      verifiedName: name,
      codeVerificationStatus: "verified",
      nameStatus: "approved",
      platformType: isCoexistence ? "smb_app" : "cloud_api",
      qualityRating: "green",
      searchVisibility: "visible",
      status: "connected",
      messagingLimitTier: "tier_10k",
    },
    businessAccount: {
      id: faker.string.numeric(15),
      name: MARKET.organizationName,
      ownerBusinessInfo: {
        id: faker.string.numeric(15),
        name: MARKET.organizationName,
        marketingMessagesOnboardingStatus: {
          status: "completed",
        },
      },
    },
  };
}

function generatePhoneConfig(
  name: string,
  provider: PhoneProvider = "du",
  isLandline: boolean = false,
): PhoneConfig {
  const phoneNumber = isLandline
    ? MARKET.organizationPhone
    : createPhoneNumber();

  return {
    phoneNumber,
    isTokenExpired: false,
    provider,
    isLandline,
    displayName: name,
    status: "active",
  };
}

/**
 * Generate mock channels for an organization
 */
export function generateChannels(
  organizationId: string,
  _workspaceId: string,
  count: number = 5,
): Channel[] {
  const channels: Channel[] = [];

  for (let i = 0; i < count; i++) {
    const config = CLINIC_CHANNELS[i] || {
      name: `Channel ${i + 1}`,
      type: "whatsapp" as ChannelType,
    };

    const createdAt = createPastDate(60 - i * 10);

    let channelConfig: InstagramConfig | WhatsappConfig | PhoneConfig;

    if (config.type === "instagram") {
      channelConfig = generateInstagramConfig();
    } else if (config.type === "phone") {
      channelConfig = generatePhoneConfig(
        config.name,
        config.provider || "du",
        config.isLandline || false,
      );
    } else {
      channelConfig = generateWhatsappConfig(
        config.name,
        config.isCoexistence ?? false,
      );
    }

    channels.push({
      id: createId(),
      name: config.name,
      imageUrl: createAvatarUrl(),
      type: config.type,
      organizationId,
      createdAt,
      updatedAt: createdAt,
      config: channelConfig,
    });
  }

  return channels;
}

/**
 * Get a channel ID by type
 */
export function getChannelByType(
  channels: Channel[],
  type: ChannelType,
): Channel | undefined {
  return channels.find((c) => c.type === type);
}

export const SHOWCASE_CHANNEL_ID = {
  MESSENGER: "mock-channel-messenger",
  TIKTOK: "mock-channel-tiktok",
  INSTAGRAM_EXPIRED: "mock-channel-instagram-expired",
  INSTAGRAM_EMPTY: "mock-channel-instagram-empty",
} as const;

/**
 * One of each remaining channel type plus the broken and never-synced states, so
 * every channel page variant renders in mock mode. Fixed values, no faker: they
 * are appended after seeding and must not shift its random sequence.
 */
export function generateShowcaseChannels(organizationId: string): Channel[] {
  const createdAt = createPastDate(5);
  const slug = MARKET.organizationSlug.replaceAll("-", "");
  const instagram = (
    id: string,
    isTokenExpired: boolean,
    mediaCount: number,
  ): InstagramConfig => ({
    isTokenExpired,
    username: `${slug}.${id.split("-").pop()}`,
    userId: "17841401234567",
    id: "17841401234567",
    mediaCount,
    profilePictureUrl: "",
    biography: "Dermatology and aesthetics. Book by DM.",
    followersCount: 1240,
    followsCount: 310,
    token: "mock-token",
  });
  const messenger: MessengerConfig = {
    pageId: "102938475610",
    pageName: MARKET.organizationName,
    pageAccessToken: "mock-token",
    pictureUrl: "",
    isTokenExpired: false,
  };
  const tiktok: TikTokConfig = {
    accessToken: "mock-token",
    refreshToken: "mock-token",
    businessId: "7012345678901234",
    username: slug,
    profileImage: "",
    bioDescription: "Skin, explained.",
    isBusinessAccount: true,
    isVerified: false,
    followingCount: 48,
    totalLikes: 23900,
    isTokenExpired: false,
  };
  const entries: Array<Pick<Channel, "id" | "name" | "type" | "config">> = [
    {
      id: SHOWCASE_CHANNEL_ID.MESSENGER,
      name: `${MARKET.organizationName} Messenger`,
      type: "messenger",
      config: messenger,
    },
    {
      id: SHOWCASE_CHANNEL_ID.TIKTOK,
      name: `${MARKET.organizationName} TikTok`,
      type: "tiktok",
      config: tiktok,
    },
    {
      id: SHOWCASE_CHANNEL_ID.INSTAGRAM_EXPIRED,
      name: "Offers Instagram",
      type: "instagram",
      config: instagram(SHOWCASE_CHANNEL_ID.INSTAGRAM_EXPIRED, true, 88),
    },
    {
      id: SHOWCASE_CHANNEL_ID.INSTAGRAM_EMPTY,
      name: "New Branch Instagram",
      type: "instagram",
      config: instagram(SHOWCASE_CHANNEL_ID.INSTAGRAM_EMPTY, false, 0),
    },
  ];

  return entries.map((entry) => ({
    ...entry,
    imageUrl: "",
    organizationId,
    createdAt,
    updatedAt: createdAt,
  }));
}
